import { useCallback, useEffect, useMemo, useRef } from "react";
import { setAppLanguage } from "../language/dico.js";
import type {
  MeetPayload,
  SyncMeta,
  SyncResult,
  ThemeColors,
} from "../types.js";
import WebsocketClient from "../websocket-client.js";

type Envelope<T> = { payload: T; syncMeta: SyncMeta };

type HandlersMap = Record<string, (payload: unknown) => void | Promise<void>>;

/** Interval between live meet snapshot retries when the meet is not yet in the gateway store. */
const LIVE_SNAPSHOT_RETRY_MS = 2_000;

/** Stop retrying live meet subscription after this duration. */
const LIVE_SNAPSHOT_RETRY_MAX_MS = 60_000;

type UseMeetSocketSyncParams<TSnapshot> = {
  meetId?: string;
  token?: string;
  onSnapshot: (snapshot: TSnapshot) => void;
  onThemeColors?: (colors: ThemeColors) => void;
  websocketUrl: string;

  /**
   * Public widget/display runtime pages: throttle `meet` requests and avoid
   * overlapping resync storms. Pair with skipping HTTP meet preload in the caller.
   */
  liveOnly?: boolean;
  /**
   * Max duration to retry live snapshot requests when the meet is not in the
   * gateway store yet. Pass `Infinity` to keep retrying (e.g. speaker view
   * waiting for Electron to enable live).
   */
  liveSnapshotRetryMaxMs?: number;
  // ex: { "athlete-patch": (patch) => ..., "group-add": (g) => ... }
  handlers: HandlersMap;
};

export function useMeetSocketSync<TSnapshot>({
  meetId,
  token,
  onSnapshot,
  onThemeColors,
  handlers,
  websocketUrl,
  liveOnly = false,
  liveSnapshotRetryMaxMs = LIVE_SNAPSHOT_RETRY_MAX_MS,
}: UseMeetSocketSyncParams<TSnapshot>) {
  const syncMetaRef = useRef<SyncMeta | null>(null);
  const resyncingRef = useRef(false);
  const hasReceivedSnapshotRef = useRef(false);
  const pendingResyncSessionIdRef = useRef<string | null>(null);
  const lastMeetEmitAtRef = useRef(0);
  const onSnapshotRef = useRef(onSnapshot);
  const onThemeColorsRef = useRef(onThemeColors);
  const handlersRef = useRef(handlers);

  onSnapshotRef.current = onSnapshot;
  onThemeColorsRef.current = onThemeColors;
  handlersRef.current = handlers;

  const meetPayload: MeetPayload | null = useMemo(() => {
    if (!meetId) return null;
    return token ? { id: meetId, auth_token: token } : { id: meetId };
  }, [meetId, token]);

  useEffect(() => {
    syncMetaRef.current = null;
    resyncingRef.current = false;
    hasReceivedSnapshotRef.current = false;
    pendingResyncSessionIdRef.current = null;
    lastMeetEmitAtRef.current = 0;
  }, [meetId]);

  const canEmitMeetRequest = useCallback(() => {
    if (!liveOnly) {
      return true;
    }
    const elapsed = Date.now() - lastMeetEmitAtRef.current;
    return elapsed >= LIVE_SNAPSHOT_RETRY_MS;
  }, [liveOnly]);

  const emitMeetSubscription = useCallback(
    (wsClient: WebsocketClient, options?: { force?: boolean }) => {
      if (!meetPayload || !meetId) return false;
      if (!options?.force && !canEmitMeetRequest()) {
        return false;
      }

      lastMeetEmitAtRef.current = Date.now();
      wsClient.emit("meet", meetPayload);
      wsClient.emit("get-theme-colors", { id: meetId });
      wsClient.emit("get-language", { id: meetId });
      return true;
    },
    [canEmitMeetRequest, meetId, meetPayload],
  );

  const requestResync = useCallback(
    (wsClient: WebsocketClient, syncMeta?: SyncMeta) => {
      if (!meetPayload) return;

      if (resyncingRef.current) {
        return;
      }

      if (
        syncMeta &&
        pendingResyncSessionIdRef.current === syncMeta.sessionId
      ) {
        return;
      }

      if (!emitMeetSubscription(wsClient, { force: !liveOnly })) {
        return;
      }

      if (syncMeta) {
        pendingResyncSessionIdRef.current = syncMeta.sessionId;
      }

      resyncingRef.current = true;
    },
    [emitMeetSubscription, liveOnly, meetPayload],
  );

  const shouldApply = useCallback(
    (r: SyncResult) =>
      r === "SNAPSHOT" || r === "REVISION" || r === "COALESCED",
    [],
  );

  const checkSyncMeta = useCallback(
    (syncMeta: SyncMeta, wsClient: WebsocketClient): SyncResult => {
      if (!meetPayload) return "RESYNC";

      if (resyncingRef.current) {
        return "RESYNC";
      }

      const last = syncMetaRef.current;

      // New session on a revision channel: wait for a full meet snapshot, do not
      // advance syncMeta from partial events (would block acceptSnapshot on rev 0).
      if (!last || syncMeta.sessionId !== last.sessionId) {
        requestResync(wsClient, syncMeta);
        return "RESYNC";
      }

      // Same revision can carry multiple event types (patch + current-athlete, etc.).
      if (syncMeta.rev === last.rev) return "COALESCED";

      // duplicate / old
      if (syncMeta.rev < last.rev) return "DUPLICATE";

      // perfect next
      if (syncMeta.rev === last.rev + 1) {
        syncMetaRef.current = syncMeta;
        return "REVISION";
      }

      // gap
      requestResync(wsClient, syncMeta);
      return "RESYNC";
    },
    [meetPayload, requestResync],
  );

  const acceptSnapshot = useCallback((syncMeta: SyncMeta) => {
    const last = syncMetaRef.current;
    if (
      hasReceivedSnapshotRef.current &&
      last &&
      syncMeta.sessionId === last.sessionId &&
      (syncMeta.rev < last.rev ||
        (syncMeta.rev === last.rev && !resyncingRef.current))
    ) {
      return false;
    }

    syncMetaRef.current = syncMeta;
    resyncingRef.current = false;
    pendingResyncSessionIdRef.current = null;
    return true;
  }, []);

  useEffect(() => {
    if (!meetId || !meetPayload) return;

    const wsClient = WebsocketClient.getInstance(websocketUrl);
    const snapshotEventName = meetId;
    const themeColorsEventName = `${meetId}/update-theme-colors`;
    const languageEventName = `${meetId}/update-language`;
    let disposed = false;
    let retryTimer: ReturnType<typeof setInterval> | null = null;
    let retryStartedAt = 0;
    const revisionListeners: Array<{
      eventName: string;
      listener: (data: unknown) => void;
    }> = [];

    const stopRetryTimer = () => {
      if (retryTimer !== null) {
        clearInterval(retryTimer);
        retryTimer = null;
      }
    };

    const requestLiveSnapshot = () => {
      if (disposed || hasReceivedSnapshotRef.current) return;
      emitMeetSubscription(wsClient, { force: true });
    };

    const snapshotListener = (data: unknown) => {
      const { payload, syncMeta } = data as Envelope<TSnapshot>;
      if (!acceptSnapshot(syncMeta)) return;
      hasReceivedSnapshotRef.current = true;
      stopRetryTimer();
      onSnapshotRef.current(payload);
    };

    const themeColorsListener = (data: unknown) => {
      onThemeColorsRef.current?.(data as ThemeColors);
    };

    const languageListener = (data: unknown) => {
      // Apply Electron meet language only when a real value was published
      if (typeof data === "string" && data.trim()) {
        setAppLanguage(data);
      }
    };

    const teardown = () => {
      wsClient.off(snapshotEventName, snapshotListener);
      wsClient.off(themeColorsEventName, themeColorsListener);
      wsClient.off(languageEventName, languageListener);
      revisionListeners.forEach(({ eventName, listener }) => {
        wsClient.off(eventName, listener);
      });
      revisionListeners.length = 0;
    };

    const bindRevisionListeners = () => {
      Object.entries(handlersRef.current).forEach(([eventKey]) => {
        const eventName = `${meetId}/${eventKey}`;
        const listener = (data: unknown) => {
          const apply = handlersRef.current[eventKey];
          if (!apply) return;
          const { payload, syncMeta } = data as Envelope<unknown>;
          const r = checkSyncMeta(syncMeta, wsClient);
          if (!shouldApply(r)) return;
          try {
            const pending = apply(payload);
            if (pending) void pending.catch(() => requestResync(wsClient));
          } catch {
            requestResync(wsClient);
          }
        };
        wsClient.on(eventName, listener);
        revisionListeners.push({ eventName, listener });
      });
    };

    const setup = () => {
      if (disposed) return;

      syncMetaRef.current = null;
      resyncingRef.current = false;
      hasReceivedSnapshotRef.current = false;
      pendingResyncSessionIdRef.current = null;

      teardown();
      bindRevisionListeners();

      wsClient.on(snapshotEventName, snapshotListener);
      wsClient.on(themeColorsEventName, themeColorsListener);
      wsClient.on(languageEventName, languageListener);

      requestLiveSnapshot();
      retryStartedAt = Date.now();
      retryTimer = setInterval(() => {
        if (hasReceivedSnapshotRef.current) {
          stopRetryTimer();
          return;
        }
        if (Date.now() - retryStartedAt >= liveSnapshotRetryMaxMs) {
          stopRetryTimer();
          return;
        }
        const elapsed = Date.now() - retryStartedAt;
        const retryDelay = Math.min(
          10000,
          LIVE_SNAPSHOT_RETRY_MS *
            2 ** Math.min(3, Math.floor(elapsed / 15000)),
        );
        if (Date.now() - lastMeetEmitAtRef.current < retryDelay) return;
        if (!emitMeetSubscription(wsClient)) {
          return;
        }
      }, LIVE_SNAPSHOT_RETRY_MS);
    };

    if (!wsClient.isConnected()) wsClient.connect();
    wsClient.on("connect", setup);
    if (wsClient.isConnected()) setup();

    return () => {
      disposed = true;
      stopRetryTimer();
      teardown();
      wsClient.off("connect", setup);
    };
  }, [
    acceptSnapshot,
    checkSyncMeta,
    emitMeetSubscription,
    liveSnapshotRetryMaxMs,
    meetId,
    meetPayload,
    shouldApply,
    websocketUrl,
  ]);

  return {
    syncMetaRef,
    resyncingRef,
    hasReceivedSnapshotRef,
  };
}
